import { CheckCircle2, Download, FilePlus2, GitBranch, Plus, Save, Settings2, Target, Trash2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmModal, Modal, promptModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { useProjects } from '../projects/api'
import { useStaffLookup } from '../projects/ui'
import { fmtDateTime, fmtMoney } from '../shared/format'
import {
  BUDGET_PERMS as P,
  downloadBudgetExcel,
  useApproveBudget,
  useBudget,
  useBudgetAccounts,
  useBudgets,
  useBudgetSettings,
  useCreateBudget,
  useImportBudget,
  useReviseBudget,
  useSaveBudget,
  useSaveBudgetSettings,
  yearMonths,
  type BudgetCreateInput,
  type BudgetDetail,
  type BudgetRow,
} from './budgets'
import { Field } from './components/FormBits'
import { useFinanceDepartments } from './departments'
import { toastError, monthShort } from './ui'

const STATUS_TONE = { draft: 'warning', approved: 'success', superseded: 'neutral' } as const
const thisYear = () => new Date().getFullYear()

/** Bütçe: yıl seçimi, sürümler, taslak düzenleme (hesap × ay; proje/departman kırılımı), onay, revizyon, Excel ve sapma eşiği. */
export function BudgetsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const [year, setYear] = useState(thisYear)
  const list = useBudgets(year)
  const [picked, setPicked] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [settings, setSettings] = useState(false)
  const items = list.data ?? []
  const selected = picked && items.some((b) => b.id === picked) ? picked : (items.find((b) => b.status === 'draft') ?? items.find((b) => b.status === 'approved') ?? items[0])?.id ?? null
  const years = Array.from({ length: 7 }, (_, i) => thisYear() - 3 + i)

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.budgets.title')}
        description={t('finance.budgets.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select className="w-28" aria-label={t('finance.budgets.year')} value={String(year)} onChange={(v) => setYear(Number(v))} options={years.map((y) => ({ value: String(y), label: String(y) }))} />
            {can(P.approve) ? (
              <Button variant="outline" onClick={() => setSettings(true)}>
                <Settings2 />
                {t('finance.budgets.settings')}
              </Button>
            ) : null}
            {can(P.edit) ? (
              <Button onClick={() => setCreating(true)}>
                <FilePlus2 />
                {t('finance.budgets.new')}
              </Button>
            ) : null}
          </div>
        }
      />
      <div className="space-y-4">
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.isLoading ? (
          <Skeleton className="h-24" />
        ) : !items.length ? (
          <EmptyState icon={Target} title={t('finance.budgets.empty', { year })} />
        ) : (
          <div className="flex flex-wrap gap-2">
            {items.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setPicked(b.id)}
                className={cn('rounded-xl border bg-card px-4 py-3 text-left shadow-card transition-colors', selected === b.id ? 'border-primary ring-1 ring-primary' : 'hover:bg-muted/40')}
              >
                <span className="flex items-center gap-2">
                  <span className="font-semibold">v{b.version}</span>
                  <Badge tone={STATUS_TONE[b.status]}>{t(`finance.budgets.status.${b.status}`)}</Badge>
                </span>
                <span className="block text-xs text-muted-foreground">{b.title}</span>
                <span className="block text-xs tabular-nums">{t('finance.budgets.expenseTotal', { amount: fmtMoney(b.expenseTotal) })}</span>
              </button>
            ))}
          </div>
        )}
        {selected ? <BudgetEditor key={selected} id={selected} onRevised={setPicked} /> : null}
      </div>
      {creating ? (
        <CreateModal
          year={year}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false)
            setPicked(id)
          }}
        />
      ) : null}
      {settings ? <SettingsModal onClose={() => setSettings(false)} /> : null}
    </>
  )
}

function BudgetEditor({ id, onRevised }: { id: string; onRevised: (id: string) => void }) {
  const q = useBudget(id)
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  if (!q.data) return <Skeleton className="h-64" />
  return <Editor key={`${q.data.id}-${q.data.events.length}`} budget={q.data} onRevised={onRevised} />
}

function Editor({ budget: b, onRevised }: { budget: BudgetDetail; onRevised: (id: string) => void }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { nameOf } = useStaffLookup()
  const accounts = useBudgetAccounts()
  const projects = useProjects()
  const departments = useFinanceDepartments()
  const save = useSaveBudget()
  const approve = useApproveBudget()
  const revise = useReviseBudget()
  const imp = useImportBudget()
  const fileRef = useRef<HTMLInputElement>(null)
  const editable = b.status === 'draft' && can(P.edit)
  const months = yearMonths(b.year)
  const [title, setTitle] = useState(b.title)
  const [rows, setRows] = useState<BudgetRow[]>(b.rows)
  const [dirty, setDirty] = useState(false)
  const setRow = (i: number, p: Partial<BudgetRow>) => {
    setRows((xs) => xs.map((r, j) => (j === i ? { ...r, ...p } : r)))
    setDirty(true)
  }
  const setCell = (i: number, m: string, v: string) => setRow(i, { months: { ...rows[i]?.months, [m]: v } })
  const rowTotal = (r: BudgetRow) => Object.values(r.months).reduce((n, v) => n + (Number(String(v).replace(',', '.')) || 0), 0)
  const kindOf = (code: string) => accounts.data?.find((a) => a.code === code)?.kind
  const total = (kind: 'income' | 'expense') => rows.filter((r) => (r.kind || kindOf(r.glCode)) === kind).reduce((n, r) => n + rowTotal(r), 0)
  const spread = async (i: number) => {
    const v = await promptModal({ title: t('finance.budgets.spreadTitle'), description: t('finance.budgets.spreadHint'), placeholder: '120000', confirmLabel: t('finance.budgets.spread') })
    const n = Number((v ?? '').replace(/\./g, '').replace(',', '.'))
    if (!n || n < 0) return
    const per = Math.floor((n / 12) * 100) / 100
    const last = Math.round((n - per * 11) * 100) / 100
    setRow(i, { months: Object.fromEntries(months.map((m, k) => [m, (k === 11 ? last : per).toFixed(2)])) })
  }
  const doSave = () =>
    save.mutate(
      { id: b.id, title, note: b.note, rows },
      {
        onSuccess: () => {
          setDirty(false)
          toast.success(t('finance.budgets.saved'))
        },
        onError: toastError,
      },
    )
  const doApprove = async () => {
    if (dirty) {
      toast.error(t('finance.budgets.saveFirst'))
      return
    }
    if (!(await confirmModal({ title: t('finance.budgets.approveTitle', { version: b.version }), description: t('finance.budgets.approveBody'), confirmLabel: t('finance.budgets.approve') }))) return
    approve.mutate({ id: b.id, note: '' }, { onSuccess: () => toast.success(t('finance.budgets.approved')), onError: toastError })
  }
  const doRevise = async () => {
    const reason = await promptModal({ title: t('finance.budgets.reviseTitle'), description: t('finance.budgets.reviseHint'), confirmLabel: t('finance.budgets.revise') })
    if (!reason?.trim()) return
    revise.mutate(
      { id: b.id, reason },
      {
        onSuccess: (d) => {
          toast.success(t('finance.budgets.revised', { version: d.version }))
          onRevised(d.id)
        },
        onError: toastError,
      },
    )
  }
  const accountOptions = (accounts.data ?? []).map((a) => ({ value: a.code, label: `${a.code} ${a.name}`, description: t(`finance.budgets.kinds.${a.kind}`) }))

  return (
    <section className="space-y-3 rounded-xl border bg-card p-4 shadow-card">
      <div className="flex flex-wrap items-center gap-2">
        {editable ? (
          <Input
            className="h-9 w-64"
            value={title}
            maxLength={160}
            onChange={(e) => {
              setTitle(e.target.value)
              setDirty(true)
            }}
          />
        ) : (
          <h2 className="font-semibold">{b.title}</h2>
        )}
        <Badge tone={STATUS_TONE[b.status]}>{t(`finance.budgets.status.${b.status}`)}</Badge>
        <span className="text-xs text-muted-foreground">
          v{b.version}
          {b.approvedAt ? ` · ${t('finance.budgets.approvedBy', { name: nameOf(b.approvedBy), at: fmtDateTime(b.approvedAt) })}` : ''}
        </span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => void downloadBudgetExcel(b).catch(toastError)}>
            <Download />
            {t('finance.budgets.excel')}
          </Button>
          {editable && can(P.import) ? (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  e.target.value = ''
                  if (f) imp.mutate({ id: b.id, file: f }, { onSuccess: () => toast.success(t('finance.budgets.imported')), onError: toastError })
                }}
              />
              <Button size="sm" variant="outline" disabled={imp.isPending} onClick={() => fileRef.current?.click()}>
                <Upload />
                {t('finance.budgets.import')}
              </Button>
            </>
          ) : null}
          {editable ? (
            <Button size="sm" disabled={!dirty || save.isPending} onClick={doSave}>
              <Save />
              {t('common.save')}
            </Button>
          ) : null}
          {b.status === 'draft' && can(P.approve) ? (
            <Button size="sm" variant="outline" disabled={approve.isPending} onClick={() => void doApprove()}>
              <CheckCircle2 />
              {t('finance.budgets.approve')}
            </Button>
          ) : null}
          {b.status === 'approved' && can(P.edit) ? (
            <Button size="sm" variant="outline" disabled={revise.isPending} onClick={() => void doRevise()}>
              <GitBranch />
              {t('finance.budgets.revise')}
            </Button>
          ) : null}
        </div>
      </div>
      {b.revisionReason ? <p className="rounded-lg bg-muted/40 p-2 text-sm">{t('finance.budgets.reason', { reason: b.revisionReason })}</p> : null}
      <div className="flex flex-wrap gap-4 text-sm">
        <span>{t('finance.budgets.incomeTotal', { amount: fmtMoney(total('income').toFixed(2)) })}</span>
        <span>{t('finance.budgets.expenseTotal', { amount: fmtMoney(total('expense').toFixed(2)) })}</span>
        <span className="font-semibold">{t('finance.budgets.netTotal', { amount: fmtMoney((total('income') - total('expense')).toFixed(2)) })}</span>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <THead>
            <TR>
              <TH className="sticky left-0 min-w-64 bg-card">{t('finance.budgets.account')}</TH>
              <TH className="min-w-40">{t('finance.budgets.project')}</TH>
              <TH className="min-w-40">{t('finance.departments.field')}</TH>
              {months.map((m) => (
                <TH key={m} className="min-w-24 text-right">
                  {monthShort(m)}
                </TH>
              ))}
              <TH className="text-right">{t('finance.pnl.total')}</TH>
              {editable ? <TH /> : null}
            </TR>
          </THead>
          <TBody>
            {rows.map((r, i) => (
              <TR key={i}>
                <TD className="sticky left-0 bg-card">
                  {editable ? (
                    <Select size="sm" searchable value={r.glCode} onChange={(glCode) => setRow(i, { glCode, kind: kindOf(glCode) ?? '' })} options={[{ value: '', label: t('common.select') }, ...accountOptions]} />
                  ) : (
                    <span className="text-sm">
                      <span className="text-xs text-muted-foreground tabular-nums">{r.glCode}</span> {r.glName}
                    </span>
                  )}
                </TD>
                <TD>
                  {editable ? (
                    <Select
                      size="sm"
                      searchable
                      value={r.projectId}
                      onChange={(projectId) => setRow(i, { projectId })}
                      options={[{ value: '', label: t('finance.budgets.allProjects') }, ...(projects.data ?? []).map((p) => ({ value: p.id, label: p.name }))]}
                    />
                  ) : (
                    <span className="text-xs">{projects.data?.find((p) => p.id === r.projectId)?.name ?? '—'}</span>
                  )}
                </TD>
                <TD>
                  {editable ? (
                    <Select
                      size="sm"
                      value={r.departmentId}
                      onChange={(departmentId) => setRow(i, { departmentId })}
                      options={[{ value: '', label: t('finance.departments.all') }, ...(departments.data ?? []).map((d) => ({ value: d.id, label: d.name }))]}
                    />
                  ) : (
                    <span className="text-xs">{departments.data?.find((d) => d.id === r.departmentId)?.name ?? '—'}</span>
                  )}
                </TD>
                {months.map((m) => (
                  <TD key={m} className="text-right tabular-nums">
                    {editable ? (
                      <Input className="h-8 w-24 text-right" inputMode="decimal" value={r.months[m] ?? ''} onChange={(e) => setCell(i, m, e.target.value)} aria-label={`${r.glCode} ${m}`} />
                    ) : r.months[m] ? (
                      fmtMoney(r.months[m])
                    ) : (
                      '—'
                    )}
                  </TD>
                ))}
                <TD className="text-right font-medium tabular-nums">{fmtMoney(rowTotal(r).toFixed(2))}</TD>
                {editable ? (
                  <TD className="whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => void spread(i)}>
                      {t('finance.budgets.spread')}
                    </Button>
                    <Button size="icon" variant="ghost" className="size-7" aria-label={t('common.delete')} onClick={() => {
                        setRows((xs) => xs.filter((_, j) => j !== i))
                        setDirty(true)
                      }}
                    >
                      <Trash2 />
                    </Button>
                  </TD>
                ) : null}
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
      {editable ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setRows((xs) => [...xs, { glCode: '', glName: '', kind: '', projectId: '', departmentId: '', months: {} }])
            setDirty(true)
          }}
        >
          <Plus />
          {t('finance.budgets.addRow')}
        </Button>
      ) : null}
      {b.events.length ? (
        <details className="text-sm">
          <summary className="cursor-pointer font-medium">{t('finance.budgets.history', { count: b.events.length })}</summary>
          <ol className="mt-2 space-y-1">
            {b.events.map((e, i) => (
              <li key={i} className="text-xs">
                <span className="tabular-nums text-muted-foreground">{fmtDateTime(e.createdAt)}</span> · {nameOf(e.userId)} ·{' '}
                {t(`finance.budgets.events.${e.kind}`, { defaultValue: e.kind })}
                {e.note ? ` — ${e.note}` : ''}
              </li>
            ))}
          </ol>
        </details>
      ) : null}
    </section>
  )
}

function CreateModal({ year, onClose, onCreated }: { year: number; onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation()
  const create = useCreateBudget()
  const [v, setV] = useState<BudgetCreateInput>({ year, title: `${year} bütçesi`, source: 'actual', sourceYear: year - 1, pct: '10', byProject: false, byDepartment: false })
  const set = (p: Partial<BudgetCreateInput>) => setV((x) => ({ ...x, ...p }))
  return (
    <Modal
      open
      size="md"
      onOpenChange={(o) => !o && onClose()}
      icon={<FilePlus2 />}
      title={t('finance.budgets.new')}
      description={t('finance.budgets.newHint')}
      footer={
        <>
          <Button variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={create.isPending} onClick={() =>
              create.mutate(v, {
                onSuccess: (d) => {
                  toast.success(t('finance.budgets.created'))
                  onCreated(d.id)
                },
                onError: toastError,
              })
            }
          >
            {t('finance.budgets.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field id="bg-year" label={t('finance.budgets.year')}>
            <Input id="bg-year" type="number" value={v.year} onChange={(e) => set({ year: Number(e.target.value) || year, title: `${Number(e.target.value) || year} bütçesi` })} />
          </Field>
          <Field id="bg-title" label={t('finance.budgets.titleField')}>
            <Input id="bg-title" value={v.title} maxLength={160} onChange={(e) => set({ title: e.target.value })} />
          </Field>
        </div>
        <Field id="bg-src" label={t('finance.budgets.source')}>
          <Select id="bg-src" value={v.source} onChange={(x) => set({ source: x as BudgetCreateInput['source'] })} options={(['actual', 'budget', 'empty'] as const).map((x) => ({ value: x, label: t(`finance.budgets.sources.${x}`) }))} />
        </Field>
        {v.source !== 'empty' ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field id="bg-sy" label={t('finance.budgets.sourceYear')}>
                <Input id="bg-sy" type="number" value={v.sourceYear} onChange={(e) => set({ sourceYear: Number(e.target.value) || v.year - 1 })} />
              </Field>
              <Field id="bg-pct" label={t('finance.budgets.pct')}>
                <Input id="bg-pct" inputMode="decimal" value={v.pct} onChange={(e) => set({ pct: e.target.value })} />
              </Field>
            </div>
            {v.source === 'actual' ? (
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="size-4 accent-[var(--color-primary)]" checked={v.byProject} onChange={(e) => set({ byProject: e.target.checked })} />
                  {t('finance.budgets.byProject')}
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="size-4 accent-[var(--color-primary)]" checked={v.byDepartment} onChange={(e) => set({ byDepartment: e.target.checked })} />
                  {t('finance.budgets.byDepartment')}
                </label>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Modal>
  )
}

function SettingsModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const q = useBudgetSettings()
  const save = useSaveBudgetSettings()
  const [pct, setPct] = useState<string | null>(null)
  const [notify, setNotify] = useState<boolean | null>(null)
  const p = pct ?? q.data?.variancePct ?? '10'
  const n = notify ?? q.data?.notify ?? true
  return (
    <Modal
      open
      size="sm"
      onOpenChange={(o) => !o && onClose()}
      icon={<Settings2 />}
      title={t('finance.budgets.settings')}
      description={t('finance.budgets.settingsHint')}
      footer={
        <>
          <Button variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={save.isPending} onClick={() =>
              save.mutate(
                { variancePct: p, notify: n },
                {
                  onSuccess: () => {
                    toast.success(t('finance.budgets.saved'))
                    onClose()
                  },
                  onError: toastError,
                },
              )
            }
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field id="bg-var" label={t('finance.budgets.variancePct')}>
          <Input id="bg-var" inputMode="decimal" value={p} onChange={(e) => setPct(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" className="size-4 accent-[var(--color-primary)]" checked={n} onChange={(e) => setNotify(e.target.checked)} />
          {t('finance.budgets.notify')}
        </label>
      </div>
    </Modal>
  )
}
